/*
 * CAPTURE-OWNED MODULE
 * Template Builder Page for capture-ui
 * Handles AI template build with progress UI.
 * Both Upload & Process and Template Studio workflows converge here.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { aiBuildTemplate, createBlankTemplate, buildTemplateFromExtraction } from '../api/captureTemplates';
import { getExtractedFields } from '../api/captureExtraction';
import { captureAPI } from '../services/api';
import { terminalOcrMessage } from '../utils/ocrReadiness';
import DocTypeSelector from '../components/template-editor/DocTypeSelector';
import './TemplateBuilderPage.css';

const BUILD_STEPS = [
  { id: 'ocr-check', label: 'Checking OCR status', cheeky: '👓 Putting on my reading glasses…' },
  { id: 'analyze', label: 'Analyzing document', cheeky: '🔍 Squinting at tiny text…' },
  { id: 'detect', label: 'Detecting fields', cheeky: '🎯 Finding the good stuff…' },
  { id: 'zones', label: 'Creating zones', cheeky: '📐 Drawing some boxes (very artistically)…' },
  { id: 'validate', label: 'Validating template', cheeky: '✅ Making sure I didn\u2019t mess up…' },
  { id: 'save', label: 'Saving template', cheeky: '💾 Saving this masterpiece…' },
];

const CHEEKY_MESSAGES = [
  '🤔 Contemplating the meaning of invoices…',
  '📊 Counting pixels with great enthusiasm…',
  '🧠 Using approximately 3 brain cells…',
  '☕ Wishing I could drink coffee…',
  '🎨 Making rectangles look professional…',
  '🔬 Analyzing at a molecular level…',
  '📝 Taking very important notes…',
  '🏗️ Building something beautiful…',
  '🎭 Pretending to be a human accountant…',
  '🚀 Going at the speed of bureaucracy…',
];

// Poll OCR word stats until words are available or timeout
async function waitForOcrReady(docId, { timeoutMs = 90000, intervalMs = 2000 } = {}) {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    let terminal = null;
    try {
      const stats = await captureAPI.getOcrWordStats({ documentId: docId });
      if (stats?.totalWords > 0) {
        console.log(`[TemplateBuilder] OCR ready: ${stats.totalWords} words (${Math.round((Date.now() - start) / 1000)}s)`);
        return stats;
      }
      // Terminal non-OCR state (e.g. duplicate_pending_review) — OCR will
      // never produce words; stop polling and report the real reason.
      terminal = terminalOcrMessage(stats?.status);
    } catch (err) {
      console.warn('[TemplateBuilder] OCR stats poll error (will retry):', err?.message);
    }
    if (terminal) throw new Error(terminal);
    await new Promise(r => setTimeout(r, intervalMs));
  }
  throw new Error('OCR is still processing. Please wait and try again.');
}

export default function TemplateBuilderPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const documentId = location.state?.documentId;
  const filename = location.state?.filename;
  const fromWorkflow = location.state?.fromWorkflow; // 'template-studio' | 'upload-process'

  const [phase, setPhase] = useState('start'); // 'start' | 'building' | 'error'
  const [buildStep, setBuildStep] = useState(-1);
  const [cheekyIdx, setCheekyIdx] = useState(0);
  const [buildError, setBuildError] = useState(null);
  const [rememberChoice, setRememberChoice] = useState(false);
  const buildingRef = useRef(false);
  const [selectedDocType, setSelectedDocType] = useState('invoice');
  const [classifying, setClassifying] = useState(false);
  const [classificationResult, setClassificationResult] = useState(null);
  const [hasExtractionResults, setHasExtractionResults] = useState(false);
  const [checkingExtraction, setCheckingExtraction] = useState(true);

  // Redirect if no documentId
  useEffect(() => {
    if (!documentId) {
      console.error('[TemplateBuilder] No document ID provided');
      navigate('/template-studio');
    }
  }, [documentId, navigate]);

  // Auto-start if user has a saved preference
  useEffect(() => {
    if (!documentId || phase !== 'start') return;
    const savedMode = localStorage.getItem('datalynks_template_build_mode');
    if (savedMode === 'ai') {
      console.log('[TemplateBuilder] Auto-starting AI build (saved preference)');
      handleStartBuild();
    } else if (savedMode === 'manual') {
      console.log('[TemplateBuilder] Auto-starting manual build (saved preference)');
      handleStartManual();
    }
    // Only run on mount — no deps on handlers (they're stable via useCallback)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, phase]);

  // Auto-classify document type after OCR is ready
  useEffect(() => {
    if (!documentId || phase !== 'start') return;
    let cancelled = false;

    const classify = async () => {
      setClassifying(true);
      try {
        // Wait for OCR words to be available
        await waitForOcrReady(documentId, { timeoutMs: 60000, intervalMs: 2000 });
        if (cancelled) return;

        // Call classification endpoint
        const result = await captureAPI.classifyDocument(documentId);
        if (cancelled) return;

        if (result?.ok && result.documentType && result.documentType !== 'unknown') {
          console.log(`[TemplateBuilder] Auto-classified as "${result.documentType}" (confidence: ${result.confidence})`);
          setSelectedDocType(result.documentType);
          setClassificationResult(result);
        } else {
          console.log('[TemplateBuilder] Classification inconclusive, defaulting to invoice');
          setClassificationResult(result || null);
        }
      } catch (err) {
        console.warn('[TemplateBuilder] Classification failed:', err?.message);
      } finally {
        if (!cancelled) setClassifying(false);
      }
    };

    classify();
    return () => { cancelled = true; };
  }, [documentId, phase]);

  // Check for existing extraction results
  useEffect(() => {
    if (!documentId) return;
    
    const checkExtraction = async () => {
      setCheckingExtraction(true);
      try {
        const result = await getExtractedFields(documentId);
        const claudeFields = (result?.fields || []).filter(f => f.source === 'claude' && (f.value_text || f.value_number));
        setHasExtractionResults(claudeFields.length >= 3);
      } catch (err) {
        console.warn('[TemplateBuilder] Could not check extraction results:', err?.message);
        setHasExtractionResults(false);
      } finally {
        setCheckingExtraction(false);
      }
    };
    
    checkExtraction();
  }, [documentId]);

  // Rotate cheeky messages while building
  useEffect(() => {
    if (phase !== 'building') return;
    const interval = setInterval(() => {
      setCheekyIdx(prev => (prev + 1) % CHEEKY_MESSAGES.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [phase]);

  const handleStartManual = useCallback(async () => {
    if (buildingRef.current) return;
    buildingRef.current = true;
    setPhase('building');
    setBuildStep(0);
    setBuildError(null);

    const templateName = (filename || '').replace(/\.[^.]+$/, '') || `Template ${new Date().toISOString().slice(0, 10)}`;

    try {
      // Save preference if checkbox is checked
      if (rememberChoice) {
        localStorage.setItem('datalynks_template_build_mode', 'manual');
      }

      // Step 0: Wait for OCR so that word snap-assist is available in the editor
      // (non-blocking: if OCR times out we still open the editor, just without snap words)
      console.log('[TemplateBuilder] Waiting for OCR (manual build)...');
      try {
        await waitForOcrReady(documentId, { timeoutMs: 60000, intervalMs: 2000 });
        setBuildStep(1);
      } catch (ocrErr) {
        console.warn('[TemplateBuilder] OCR wait timed out for manual build — continuing anyway:', ocrErr?.message);
        setBuildStep(1);
      }

      // Step 1: Create blank template
      setBuildStep(2);
      const res = await createBlankTemplate(documentId, { templateName, documentType: selectedDocType });
      const templateId = res?.template?.id;
      if (!templateId) throw new Error('Template created but no ID returned');

      console.log('[TemplateBuilder] Blank template created:', templateId);
      navigate(`/template-studio?openTemplate=${templateId}`, { replace: true });
    } catch (err) {
      console.error('[TemplateBuilder] Manual create failed:', err);
      const msg = err?.response?.data?.error || err?.message || 'Failed to create template';
      setBuildError(msg);
      setPhase('error');
    } finally {
      buildingRef.current = false;
    }
  }, [documentId, filename, navigate, rememberChoice, selectedDocType]);

  const handleStartBuild = useCallback(async () => {
    if (buildingRef.current) return;
    buildingRef.current = true;
    // Save preference if checkbox is checked
    if (rememberChoice) {
      localStorage.setItem('datalynks_template_build_mode', 'ai');
    }
    setPhase('building');
    setBuildStep(0);
    setBuildError(null);

    const sleep = (ms) => new Promise(r => setTimeout(r, ms));
    const templateName = (filename || '').replace(/\.[^.]+$/, '') || `Template ${new Date().toISOString().slice(0, 10)}`;

    try {
      // Step 0: Check OCR
      console.log('[TemplateBuilder] Checking OCR for doc:', documentId);
      await waitForOcrReady(documentId);
      setBuildStep(1); // Analyzing
      await sleep(400);

      // Step 1-2: AI Build (covers analyze + detect)
      setBuildStep(2); // Detecting fields
      console.log('[TemplateBuilder] Starting AI build for doc:', documentId, 'name:', templateName);

      let buildRes;
      try {
        buildRes = await aiBuildTemplate(documentId, { templateName, documentType: selectedDocType });
      } catch (buildErr) {
        // If ai-build says no OCR words, retry once
        const errMsg = buildErr?.response?.data?.error || buildErr?.message || '';
        if (errMsg.toLowerCase().includes('no ocr words')) {
          console.warn('[TemplateBuilder] AI build got "No OCR words", retrying after extra wait…');
          setBuildStep(0);
          await waitForOcrReady(documentId, { timeoutMs: 30000, intervalMs: 2000 });
          setBuildStep(2);
          buildRes = await aiBuildTemplate(documentId, { templateName, documentType: selectedDocType });
        } else {
          throw buildErr;
        }
      }

      const templateId = buildRes?.template?.id;
      if (!templateId) throw new Error('AI build succeeded but no template ID returned');

      // Steps 3-5: visual progress
      setBuildStep(3); // Creating zones
      await sleep(600);
      setBuildStep(4); // Validating
      await sleep(400);
      setBuildStep(5); // Saving
      await sleep(300);

      console.log('[TemplateBuilder] AI build complete, template:', templateId);

      // Navigate to Template Studio with auto-open
      navigate(`/template-studio?openTemplate=${templateId}`, { replace: true });

    } catch (err) {
      console.error('[TemplateBuilder] Build failed:', err);
      const msg = err?.response?.data?.error || err?.serverDetails?.error || err?.message || 'Failed to build template';
      setBuildError(msg);
      setPhase('error');
    } finally {
      buildingRef.current = false;
    }
  }, [documentId, filename, navigate, rememberChoice, selectedDocType]);

  const handleBuildFromExtraction = useCallback(async () => {
    if (buildingRef.current) return;
    buildingRef.current = true;
    setPhase('building');
    setBuildStep(0);
    setBuildError(null);

    const templateName = (filename || '').replace(/\.[^.]+$/, '') || `Template ${new Date().toISOString().slice(0, 10)}`;

    try {
      setBuildStep(1);
      console.log('[TemplateBuilder] Building template from extraction results:', documentId);
      
      const res = await buildTemplateFromExtraction(documentId, {
        templateName,
        documentType: selectedDocType,
      });
      
      const templateId = res?.templateId;
      if (!templateId) throw new Error('Template created but no ID returned');

      setBuildStep(3);
      await new Promise(r => setTimeout(r, 300));
      setBuildStep(5);
      await new Promise(r => setTimeout(r, 200));

      console.log('[TemplateBuilder] Template from extraction complete:', templateId);
      navigate(`/template-studio?openTemplate=${templateId}`, { replace: true });

    } catch (err) {
      console.error('[TemplateBuilder] Build from extraction failed:', err);
      const msg = err?.response?.data?.error || err?.message || 'Failed to build template from extraction';
      setBuildError(msg);
      setPhase('error');
    } finally {
      buildingRef.current = false;
    }
  }, [documentId, filename, navigate, selectedDocType]);

  const handleCancel = () => {
    if (fromWorkflow === 'upload-process') {
      navigate('/');
    } else {
      navigate('/template-studio');
    }
  };

  const handleRetry = () => {
    setBuildError(null);
    setPhase('start');
    setBuildStep(-1);
  };

  if (!documentId) return null;

  // ── Start screen ──
  if (phase === 'start') {
    return (
      <div className="tb-page">
        <div className="tb-start">
          <div className="tb-start-card">
            <h2>Create Template</h2>
            <p className="tb-filename">From: {filename || 'uploaded document'}</p>

            <div className="tb-doctype-selector">
              {classifying ? (
                <>
                  <label>Document Type:</label>
                  <span className="tb-classifying">Identifying document type…</span>
                </>
              ) : (
                <DocTypeSelector
                  value={selectedDocType}
                  onChange={setSelectedDocType}
                />
              )}
              {classificationResult && !classifying && (
                <span className="tb-confidence-hint">
                  {classificationResult.confidence >= 0.7
                    ? '✓ High confidence'
                    : classificationResult.confidence >= 0.5
                    ? '~ Moderate confidence'
                    : '? Low confidence — please verify'}
                </span>
              )}
            </div>

            <div className="tb-choice-grid">
              {/* Build from Extraction — only shown when Claude results exist */}
              {hasExtractionResults && (
                <div className="tb-choice-card tb-choice-card--featured">
                  <div className="tb-choice-icon">⚡</div>
                  <h3>Build from Extraction</h3>
                  <p>
                    This document was already processed by AI. Build a semantic template
                    instantly using the extraction results — no additional AI processing needed.
                  </p>
                  <p className="tb-choice-detail">Uses anchor text Claude already identified.</p>
                  <p className="tb-choice-time">Takes under 2 seconds</p>
                  <button className="tb-btn tb-btn-featured" onClick={handleBuildFromExtraction}>
                    Build Instantly
                  </button>
                </div>
              )}

              {/* AI Build option */}
              <div className="tb-choice-card">
                <div className="tb-choice-icon">🤖</div>
                <h3>AI Build</h3>
                <p>
                  Claude analyzes the document and automatically creates extraction zones
                  for common fields like vendor name, invoice number, date, totals, and line items.
                </p>
                <p className="tb-choice-detail">Best for invoices, POs, and structured forms.</p>
                <p className="tb-choice-time">Takes 10–15 seconds (re-analyzes document)</p>
                <button className="tb-btn tb-btn-primary" onClick={handleStartBuild}>
                  Start AI Build
                </button>
              </div>

              {/* Manual Build option */}
              <div className="tb-choice-card">
                <div className="tb-choice-icon">✏️</div>
                <h3>Build Manually</h3>
                <p>
                  Create a blank template and define your own fields and extraction zones
                  using the Template Editor.
                </p>
                <p className="tb-choice-detail">Best for custom layouts or unique document types.</p>
                <p className="tb-choice-time">You control the pace</p>
                <button className="tb-btn tb-btn-secondary" onClick={handleStartManual}>
                  Start Manual Build
                </button>
              </div>
            </div>

            <div className="tb-choice-footer">
              <label className="tb-remember-label">
                <input
                  type="checkbox"
                  checked={rememberChoice}
                  onChange={(e) => setRememberChoice(e.target.checked)}
                />
                <span>Remember my choice for next time</span>
              </label>
              <button className="tb-btn-link" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Error screen ──
  if (phase === 'error') {
    return (
      <div className="tb-page">
        <div className="tb-start">
          <div className="tb-start-card">
            <h2>Build Failed</h2>
            <div className="tb-error-box">
              <p>{buildError}</p>
            </div>
            <div className="tb-start-actions">
              <button className="tb-btn tb-btn-secondary" onClick={handleCancel}>
                Go Back
              </button>
              <button className="tb-btn tb-btn-primary" onClick={handleRetry}>
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Building screen ──
  const progress = buildStep >= 0 ? Math.min(((buildStep + 1) / BUILD_STEPS.length) * 100, 100) : 0;

  return (
    <div className="tb-page">
      <div className="tb-building">
        <div className="tb-building-card">
          <h2>Building Template</h2>
          <p className="tb-filename">From: {filename || 'uploaded document'}</p>
          {localStorage.getItem('datalynks_template_build_mode') && (
            <button
              className="tb-btn-link tb-reset-pref"
              onClick={(e) => {
                e.preventDefault();
                localStorage.removeItem('datalynks_template_build_mode');
                setPhase('start');
                setBuildStep(-1);
                buildingRef.current = false;
              }}
            >
              ← Change build method
            </button>
          )}

          {/* Progress bar */}
          <div className="tb-progress-bar">
            <div className="tb-progress-fill" style={{ width: `${progress}%` }} />
          </div>

          {/* Steps */}
          <div className="tb-steps">
            {BUILD_STEPS.map((step, i) => {
              const isDone = buildStep > i;
              const isActive = buildStep === i;
              return (
                <div key={step.id} className={`tb-step ${isDone ? 'tb-step--done' : ''} ${isActive ? 'tb-step--active' : ''}`}>
                  <div className="tb-step-icon">
                    {isDone ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : isActive ? (
                      <div className="tb-spinner-sm" />
                    ) : (
                      <div className="tb-step-dot" />
                    )}
                  </div>
                  <span className="tb-step-label">{step.label}</span>
                  {isActive && <span className="tb-step-cheeky">{step.cheeky}</span>}
                </div>
              );
            })}
          </div>

          {/* Rotating cheeky message */}
          <div className="tb-cheeky-footer" key={cheekyIdx}>
            {CHEEKY_MESSAGES[cheekyIdx]}
          </div>
        </div>
      </div>
    </div>
  );
}
